import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getMilestones, type Task, type Milestone } from '../../packages/api-client/pm-api';
import { GanttWithTable } from '../../components/GanttTable';
import { StateView } from '../../packages/ui';
import { getAllTasks, planErrorMessage } from './planData';
import './PlanView.css';

/**
 * TimelineView — Planning › Timeline: the project Gantt (every dated task and
 * milestone) with a Table view alternative. Bars and table rows open the task.
 * Rendered inside the project shell; /projects/:id/timeline redirects here.
 */
export function TimelineView({ projectId }: { projectId: string }) {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [truncated, setTruncated] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([getAllTasks(projectId), getMilestones(projectId)])
            .then(([t, m]) => { setTasks(t.tasks); setTruncated(t.truncated); setMilestones(m); setError(null); })
            .catch(err => setError(planErrorMessage(err, 'load the timeline')))
            .finally(() => setLoading(false));
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <StateView state="loading" label="Loading timeline" lines={6} />;
    if (error) return <StateView state="error" title="The timeline couldn't be loaded" message={error} onRetry={load} />;

    const dated = milestones.filter(m => m.due_date).length + tasks.filter(t => t.start_date || t.due_date).length;
    const undated = milestones.length + tasks.length - dated;

    return (
        <div className="plan-view">
            {truncated && (
                <p role="status" className="plan-notice plan-notice--warn">
                    Showing the first 10,000 tasks. Tasks beyond that are not on this timeline.
                </p>
            )}
            <p className="plan-muted">
                {dated} dated item{dated !== 1 ? 's' : ''}
                {undated > 0 && ` · ${undated} without dates not shown (set a start or due date to place them)`}
            </p>
            <GanttWithTable tasks={tasks} milestones={milestones} onBarClick={(id) => navigate(`/tasks/${id}`)} />
        </div>
    );
}

/**
 * GanttPage — the old standalone /projects/:id/timeline route. It used to render
 * its own project header + a stubby two-item tab bar, which dropped the full
 * project-detail chrome (Overview/Tasks/Board/…) and forced a Back click to
 * return. The timeline now lives as an in-page tab inside ProjectDetailPage, so
 * this route just redirects there, forwarding any existing query params.
 */
export function GanttPage() {
    const { id: projectId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!projectId) return;
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'Timeline');
        navigate(`/projects/${projectId}?${next.toString()}`, { replace: true });
    }, [projectId, searchParams, navigate]);

    return null;
}
